import { z } from 'zod';

import { InfrastructureType, updateInfrastructureSubItemsDto } from '@nohara-dxdev/mt-common';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import { getInfrastructureOfSubItemsQueryKey } from './get-infrastructure-of-sub-items';
import { getInfrastructureSubItemsListQueryKey } from './get-infrastructure-sub-items-list';

type UpdateInfrastructureSubItemsDto = z.infer<typeof updateInfrastructureSubItemsDto>;

interface UpdateInfrastructureSubItemsParams {
  parentItemId: string;
  infrastructureType: InfrastructureType;
  childItemId: string;
  subItems: UpdateInfrastructureSubItemsDto;
}

export const updateInfrastructureSubItems = async (
  orgId: string,
  projectId: string,
  { parentItemId, infrastructureType, childItemId, subItems }: UpdateInfrastructureSubItemsParams,
): Promise<void> => {
  return api.post(
    `/orgs/${orgId}/projects/${projectId}/items/${parentItemId}/infrastructures/${infrastructureType}/${childItemId}/sub-items`,
    subItems,
  );
};

export const useUpdateInfrastructureSubItems = (
  options?: Omit<UseMutationOptions<void, Error, UpdateInfrastructureSubItemsParams>, 'mutationFn'>,
) => {
  const { orgId, projectId } = useScopeIds('org', 'project');
  const queryClient = useQueryClient();

  const { onSuccess, onError, ...restConfig } = options ?? {};

  return useMutation({
    mutationFn: (params: UpdateInfrastructureSubItemsParams) =>
      updateInfrastructureSubItems(orgId, projectId, params),
    onSuccess: (_, variables, ...args) => {
      // Invalidate the sub-items list query
      void queryClient.invalidateQueries({
        queryKey: getInfrastructureSubItemsListQueryKey(
          orgId,
          projectId,
          variables.parentItemId,
          variables.infrastructureType,
          variables.childItemId,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: getInfrastructureOfSubItemsQueryKey(orgId, projectId),
      });
      onSuccess?.(_, variables, ...args);
    },
    onError: (error, variables, ...args) => {
      onError?.(error, variables, ...args);
    },
    ...restConfig,
  });
};
